import Phaser from 'phaser'
import {
	getPersistentGameOverSummary,
	setPersistentGameOverSummary,
	clearPersistentGameOverSummary,
	getPersistentRankingRunSession,
	setPersistentRankingRunSession,
	clearPersistentRankingRunSession
} from './EventCenter'
import RankingApiClient, { type DailyRankingRow } from '../services/RankingApiClient'
import { isLocalSessionId, enqueueFinishOperation } from '../services/OfflineRankingQueue'

const DEFAULT_RANKING_API_BASE_URL = '/ranking/api/v1'
const DEFAULT_NICKNAME = 'SLAYER'
const TOP_RANKING_LIMIT = 5
const MENU_BACKGROUND_MUSIC_KEY = 'music-menu-fondo'
const MENU_BACKGROUND_MUSIC_PATH = 'assets/musica/fondo.mp3'
const MENU_BACKGROUND_MUSIC_VOLUME = 0.18

export default class GameOver extends Phaser.Scene
{
	private statusText?: Phaser.GameObjects.Text
	private summaryText?: Phaser.GameObjects.Text
	private rankingText?: Phaser.GameObjects.Text
	private rankingApi?: RankingApiClient
	private backgroundMusic?: Phaser.Sound.BaseSound

	constructor()
	{
		super('game-over')
	}

	preload()
	{
		if (!this.cache.audio.exists(MENU_BACKGROUND_MUSIC_KEY))
		{
			this.load.audio(MENU_BACKGROUND_MUSIC_KEY, MENU_BACKGROUND_MUSIC_PATH)
		}
	}

	create()
	{
		const apiBaseUrlFromRegistry = this.game.registry.get('rankingApiBaseUrl')
		this.rankingApi = new RankingApiClient(typeof apiBaseUrlFromRegistry === 'string'
			? apiBaseUrlFromRegistry
			: DEFAULT_RANKING_API_BASE_URL)

		this.startBackgroundMusic()

		const summary = getPersistentGameOverSummary() ?? {
			nickname: DEFAULT_NICKNAME,
			finalBudget: 0,
			finalMaxBudget: 3000,
			elapsedMs: 0
		}

		const { width, height } = this.scale

		this.add.text(width * 0.5, height * 0.28, 'Game Over', {
			fontFamily: '"Press Start 2P", system-ui',
			fontSize: '46px',
			color: '#ff0000'
		}).setOrigin(0.5)

		this.summaryText = this.add.text(width * 0.5, height * 0.42, this.buildSummaryText(summary), {
			fontFamily: '"Press Start 2P", system-ui',
			fontSize: '12px',
			color: '#ffffff',
			align: 'center'
		}).setOrigin(0.5)

		this.statusText = this.add.text(width * 0.5, height * 0.54, 'Enviando resultado al ranking...', {
			fontFamily: '"Press Start 2P", system-ui',
			fontSize: '10px',
			color: '#f7ca63',
			align: 'center'
		}).setOrigin(0.5)

		this.rankingText = this.add.text(width * 0.5, height * 0.68, 'Cargando top diario...', {
			fontFamily: '"Press Start 2P", system-ui',
			fontSize: '10px',
			color: '#f4f0e8',
			align: 'center'
		}).setOrigin(0.5)

		void this.finishRunAndLoadRanking(summary)

		const button = this.add.rectangle(width * 0.5, height * 0.9, 220, 64, 0xffffff)
			.setInteractive()
			.on(Phaser.Input.Events.GAMEOBJECT_POINTER_UP, () => {
				clearPersistentRankingRunSession()
				clearPersistentGameOverSummary()
				this.scene.stop('logo-overlay')
				this.scene.start('intro')
			})

		this.add.text(button.x, button.y, 'Play Again', {
			fontFamily: '"Press Start 2P", system-ui',
			color: '#000000'
		}).setOrigin(0.5)

		this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
			this.stopBackgroundMusic()
		})
	}

	private startBackgroundMusic()
	{
		if (this.backgroundMusic?.isPlaying)
		{
			return
		}

		if (!this.cache.audio.exists(MENU_BACKGROUND_MUSIC_KEY))
		{
			return
		}

		const existingMusic = this.sound.get(MENU_BACKGROUND_MUSIC_KEY)
		if (existingMusic)
		{
			this.backgroundMusic = existingMusic
			if (!existingMusic.isPlaying)
			{
				existingMusic.play({ loop: true, volume: MENU_BACKGROUND_MUSIC_VOLUME })
			}
			return
		}

		this.backgroundMusic = this.sound.add(MENU_BACKGROUND_MUSIC_KEY, {
			loop: true,
			volume: MENU_BACKGROUND_MUSIC_VOLUME
		})
		this.backgroundMusic.play()
	}

	private stopBackgroundMusic()
	{
		if (this.backgroundMusic)
		{
			this.backgroundMusic.stop()
			this.backgroundMusic.destroy()
			this.backgroundMusic = undefined
			return
		}

		const existingMusic = this.sound.get(MENU_BACKGROUND_MUSIC_KEY)
		if (existingMusic)
		{
			existingMusic.stop()
			existingMusic.destroy()
		}
	}

	private buildSummaryText(summary: { nickname: string, finalBudget: number, elapsedMs: number }): string
	{
		return [
			`Apodo: ${summary.nickname}`,
			`Score: ${Math.round(summary.finalBudget)}€`,
			`Tiempo: ${this.formatElapsedTime(summary.elapsedMs)}`
		].join('\n')
	}

	private async finishRunAndLoadRanking(summary: { nickname: string, finalBudget: number, finalMaxBudget: number, elapsedMs: number })
	{
		const session = getPersistentRankingRunSession()
		if (!session || !this.rankingApi)
		{
			this.statusText?.setText('Ranking no disponible (sin sesion API).')
			this.rankingText?.setText('')
			return
		}

		const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
		const today = this.getDateInTimezone(timezone)

		let computedScore = summary.finalBudget
		let integrityStatus = 'unknown'
		let dailyPosition: number | null = null
		let flags: string[] = []
		let rankingRowsForSummary: Array<{ position: number, nickname: string, score: number, durationMs: number }> | undefined

		if (isLocalSessionId(session.runId))
		{
			if (!session.finished)
			{
				enqueueFinishOperation(session.runId, {
					seq_final: Math.max(1, session.seq),
					end_reason: 'game_over',
					client_ended_at_ms: Date.now(),
					final_budget: summary.finalBudget,
					final_max_budget: summary.finalMaxBudget,
					tz: timezone,
					summary: {
						nickname: summary.nickname,
						elapsed_ms: summary.elapsedMs
					}
				})
				setPersistentRankingRunSession({
					...session,
					finished: true
				})
			}

			this.statusText?.setText('Sin conexion: el resultado se enviara cuando vuelva la red.')
			this.rankingText?.setText('Ranking no disponible sin conexion.')

			setPersistentGameOverSummary({
				nickname: summary.nickname,
				finalBudget: summary.finalBudget,
				finalMaxBudget: summary.finalMaxBudget,
				elapsedMs: summary.elapsedMs,
				computedScore,
				dailyPosition,
				integrityStatus,
				flags,
				runId: session.runId,
				rankingRows: rankingRowsForSummary
			})
			return
		}

		if (!session.finished)
		{
			try
			{
				const finishResponse = await this.rankingApi.finishRun({
					run_id: session.runId,
					seq_final: Math.max(1, session.seq),
					end_reason: 'game_over',
					client_ended_at_ms: Date.now(),
					final_budget: summary.finalBudget,
					final_max_budget: summary.finalMaxBudget,
					tz: timezone,
					summary: {
						nickname: summary.nickname,
						elapsed_ms: summary.elapsedMs
					}
				}, session.runToken)

				computedScore = finishResponse.computed_score
				integrityStatus = finishResponse.integrity_status
				dailyPosition = finishResponse.daily_position ?? null
				flags = finishResponse.flags ?? []

				setPersistentRankingRunSession({
					...session,
					finished: true
				})
			}
			catch (error)
			{
				// console.warn('[ranking-api] finish failed, queuing for later sync', error)
				enqueueFinishOperation(session.runId, {
					seq_final: Math.max(1, session.seq),
					end_reason: 'game_over',
					client_ended_at_ms: Date.now(),
					final_budget: summary.finalBudget,
					final_max_budget: summary.finalMaxBudget,
					tz: timezone,
					summary: {
						nickname: summary.nickname,
						elapsed_ms: summary.elapsedMs
					}
				}, session.runToken)
				setPersistentRankingRunSession({
					...session,
					finished: true
				})
			}
		}

		try
		{
			const [dailyRanking, myRanking] = await Promise.all([
				this.rankingApi.getDailyRanking(today, timezone, TOP_RANKING_LIMIT),
				this.rankingApi.getMyRanking(session.runId, timezone)
			])

			dailyPosition = myRanking.position
			rankingRowsForSummary = dailyRanking.ranking.map(row => ({
				position: row.position,
				nickname: row.nickname,
				score: row.score,
				durationMs: row.duration_ms
			}))
			this.rankingText?.setText(this.formatRanking(dailyRanking.ranking))
		}
		catch (error)
		{
			// console.warn('[ranking-api] ranking query failed', error)
			this.rankingText?.setText('No se pudo cargar el ranking diario.')
		}

		const integritySuffix = integrityStatus !== 'unknown'
			? ` | Integridad: ${integrityStatus}`
			: ''
		const flagsSuffix = flags.length > 0 ? ` | Flags: ${flags.length}` : ''
		const positionSuffix = dailyPosition ? `Posicion: #${dailyPosition}` : 'Posicion: --'
		this.statusText?.setText(`${positionSuffix} | Score validado: ${Math.round(computedScore)}€${integritySuffix}${flagsSuffix}`)

		setPersistentGameOverSummary({
			nickname: summary.nickname,
			finalBudget: summary.finalBudget,
			finalMaxBudget: summary.finalMaxBudget,
			elapsedMs: summary.elapsedMs,
			computedScore,
			dailyPosition,
			integrityStatus,
			flags,
			runId: session.runId,
			rankingRows: rankingRowsForSummary
		})
	}

	private formatRanking(rows: DailyRankingRow[]): string
	{
		if (rows.length === 0)
		{
			return 'Sin partidas registradas hoy.'
		}

		const lines = ['Top diario:']
		rows.forEach(row => {
			lines.push(`#${row.position} ${row.nickname} ${Math.round(row.score)}€ ${this.formatElapsedTime(row.duration_ms)}`)
		})

		return lines.join('\n')
	}

	private formatElapsedTime(elapsedMs: number): string
	{
		const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000))
		const minutes = Math.floor(totalSeconds / 60)
		const seconds = totalSeconds % 60
		return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
	}

	private getDateInTimezone(timeZone: string): string
	{
		const formatter = new Intl.DateTimeFormat('en-CA', {
			timeZone,
			year: 'numeric',
			month: '2-digit',
			day: '2-digit'
		})

		return formatter.format(new Date())
	}
}
